import { useState, useEffect } from "react";
import { ethers } from "ethers";
import { ClipLoader } from "react-spinners";
import Cookies from "js-cookie";
import "./styles/FaucetForm.css";
import { jwtDecode } from "jwt-decode";

import LoginModal from "./loginModal";
import ErrorRoleModal from "./errorRoleModal";

// Helper: convert wei string to PC display (divide by 1e18)
function weiToPC(weiStr: string | undefined): string {
  if (!weiStr || weiStr === '0') return '0';
  try {
    // Pad to at least 19 chars to handle division by 1e18
    const padded = weiStr.padStart(19, '0');
    const intPart = padded.slice(0, padded.length - 18) || '0';
    const decPart = padded.slice(padded.length - 18, padded.length - 14); // 4 decimal places
    return `${intPart}.${decPart}`;
  } catch {
    return '0';
  }
}

interface TokenPayload {
  exp: number;
  client_id: string;
  redirect_uri: string;
  username: string;
  mm_address: string;
  type: string;
  random_string: string;
}
declare global {
  interface Window {
    PentagonWidget?: {
      mount: () => void;
    };
  }
}

const FaucetForm = (props: any) => {
  const [inputAddress, setInputAddress] = useState<string>("");
  const [address, setAddress] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<boolean>(false);
  const validRoles = ["VIP1", "VIP2", "VIP3"];
  const [shouldAllowSend, setShouldAllowSend] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [claimStatus, setClaimStatus] = useState<string>("");
  const [sendTokenResponse, setSendTokenResponse] = useState<any>({
    txHash: null,
    message: null,
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokenLoading, setTokenLoading] = useState<boolean>(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [poolStatus, setPoolStatus] = useState<any>(null);
  const [detectedTier, setDetectedTier] = useState<string | null>(null);
  const [penBalance, setPenBalance] = useState<number | null>(null);
  const [penBalanceEth, setPenBalanceEth] = useState<number | null>(null);
  const [onChainTier, setOnChainTier] = useState<string | null>(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState<number>(0);
  const [loyaltyDays, setLoyaltyDays] = useState<number>(0);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);
  const [keeperPreview, setKeeperPreview] = useState<any>(null);
  const [topUpLoading, setTopUpLoading] = useState<boolean>(false);
  const [topUpResult, setTopUpResult] = useState<string | null>(null);

  // Check rate limit when address changes
  useEffect(() => {
    if (address) {
      checkRateLimit();
    }
  }, [address]);

  // Fetch pool status and keeper preview on mount
  useEffect(() => {
    fetchPoolStatus();
    fetchKeeperPreview();
  }, []);

  // Make REQUEST button enabled if user is authenticated with valid address and on-chain eligible
  useEffect(() => {
    if (address && onChainTier) {
      setShouldAllowSend(true);
      return;
    }
    setShouldAllowSend(false);
  }, [address, onChainTier]);

  const checkRateLimit = async () => {
    const walletAddress = Cookies.get("wallet_address");
    if (!walletAddress) return;

    try {
      const res = await fetch("/api/faucet/check", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          address: walletAddress,
          chain: "pentagon",
        }),
      });

      const data = await res.json();

      if (data.isRateLimited) {
        const hours = Math.floor(data.retryAfterMs / (1000 * 60 * 60));
        const minutes = Math.floor(
          (data.retryAfterMs % (1000 * 60 * 60)) / (1000 * 60)
        );
        setRetryMessage(`You can only request once in 24 hours. Please try again later after ${hours} hours ${minutes} minutes.`);
      } else {
        setRetryMessage(null);
      }
    } catch (error) {
      console.error("Rate limit check failed:", error);
      setRetryMessage(null); // Fallback: assume no limit
    }
  };

  // Check user's PEN balance on Pentagon Chain
  async function checkPenBalance(walletAddr: string): Promise<void> {
    try {
      const res = await fetch(`/api/penBalance?address=${walletAddr}`);
      const data = await res.json();

      const pcPen = parseInt(data.penPC || "0");
      const ethPen = parseInt(data.penETH || "0");

      setPenBalance(pcPen);
      setPenBalanceEth(ethPen);
      setLoyaltyPoints(data.loyaltyPoints || 0);
      setLoyaltyDays(data.daysHeld || 0);
      setCooldownSeconds(data.cooldownSeconds || 0);

      if (pcPen >= 5_000_000) setOnChainTier("VIP3");
      else if (pcPen >= 1_000_000) setOnChainTier("VIP2");
      else if (pcPen >= 200_000) setOnChainTier("VIP1");
      else setOnChainTier(null);
    } catch (err) {
      console.error("Failed to check PEN balance:", err);
      setPenBalance(null);
      setPenBalanceEth(null);
      setOnChainTier(null);
    }
  }

  // Check PEN balance when address changes
  useEffect(() => {
    if (address) {
      checkPenBalance(address);
    } else {
      setPenBalance(null);
      setPenBalanceEth(null);
      setOnChainTier(null);
    }
  }, [address]);

  async function fetchPoolStatus(): Promise<void> {
    try {
      const response = await fetch('/api/poolStatus');
      const data = await response.json();
      setPoolStatus(data);
    } catch (err) {
      console.error('Failed to fetch pool status:', err);
    }
  }

  async function fetchKeeperPreview(): Promise<void> {
    try {
      const provider = new ethers.providers.JsonRpcProvider("https://rpc.pentagon.games");
      const keeper = new ethers.Contract(KEEPER_ADDRESS, KEEPER_ABI, provider);
      const [delta, toSend, keeperBalance, canTopUp] = await keeper.preview();
      const lastTime = await keeper.lastTopUpTime();
      const totalSent = await keeper.totalTransferred();
      setKeeperPreview({
        delta: delta.toString(),
        toSend: toSend.toString(),
        keeperBalance: keeperBalance.toString(),
        canTopUp,
        lastTopUpTime: Number(lastTime),
        totalTransferred: totalSent.toString(),
      });
    } catch (err) {
      console.error('Failed to fetch keeper preview:', err);
    }
  }

  async function handleTopUp(): Promise<void> {
    setTopUpLoading(true);
    setTopUpResult(null);
    try {
      if (!window.ethereum) {
        setTopUpResult("Please connect a wallet (MetaMask, Rabby, etc.)");
        return;
      }
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const network = await provider.getNetwork();
      if (network.chainId !== 3344) {
        setTopUpResult("Please switch to Pentagon Chain (chain ID 3344)");
        return;
      }
      const signer = provider.getSigner();
      const keeper = new ethers.Contract(KEEPER_ADDRESS, KEEPER_ABI, signer);
      const tx = await keeper.topUp();
      setTopUpResult("Transaction submitted. Waiting for confirmation...");
      await tx.wait();
      setTopUpResult("Top-up successful! Pool replenished.");
      fetchPoolStatus();
      fetchKeeperPreview();
    } catch (err: any) {
      const msg = err?.reason || err?.message || "Top-up failed";
      if (msg.includes("Too soon")) {
        setTopUpResult("Top-up already done this week. Try again after 6 days.");
      } else if (msg.includes("No new gas")) {
        setTopUpResult("No new gas fees collected since last top-up.");
      } else if (msg.includes("underfunded")) {
        setTopUpResult("Keeper contract needs more PC. Contact the team.");
      } else {
        setTopUpResult(msg.slice(0, 120));
      }
    } finally {
      setTopUpLoading(false);
    }
  }

  async function checkRole(): Promise<void> {
    const token = Cookies.get("auth_token");

    if (!token) {
      console.error("Authentication token missing");
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('token', token);

      const fetchResponse = await fetch('https://api.account.pentagon.games/sso/user_roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      const data = await fetchResponse.json();
      const roles: string[] = data?.discord_roles || [];
      const hasValidRole = roles.some((role: string) => validRoles.includes(role));
      setUserRole(!hasValidRole);

      // Detect highest tier for display
      if (roles.includes('VIP3')) {
        setDetectedTier('VIP3');
      } else if (roles.includes('VIP2')) {
        setDetectedTier('VIP2');
      } else if (roles.includes('VIP1')) {
        setDetectedTier('VIP1');
      } else {
        setDetectedTier(null);
      }

      if (!data?.role) {
        console.error("Role not found in response");
        return;
      }

    } catch (err) {
      console.error("Failed to fetch user role from external SSO:", err);
    }
  }


  function updateAddress(addr: any): void {
    setInputAddress(addr!);

    if (addr) {
      if (ethers.utils.isAddress(addr)) {
        setAddress(addr);
      } else {
        setAddress(null);
      }
    } else if (address != null) {
      setAddress(null);
    }
  }

  async function sendToken(): Promise<void> {
    if (!shouldAllowSend || userRole || retryMessage || cooldownSeconds > 0) {
      return;
    }
    let data: any;
    try {
      setIsLoading(true);
      setClaimStatus("Submitting claim...");

      const auth_token = Cookies.get("auth_token");

      setClaimStatus("Verifying VIP status...");

      const response = await props.axios.post(props.config.api.sendToken, {
        address,
        chain: "pentagon",
        auth_token,
      });

      if (response.status === 401) {
        handleTokenExpiration();
        return;
      }
      data = response?.data;
    } catch (err: any) {
      data = err?.response?.data || err;
      if (err?.response?.status === 401) {
        handleTokenExpiration();
        return;
      }
    }

    if (data?.txHash) {
      setClaimStatus("✅ PC sent!");
    } else if (data?.message) {
      setClaimStatus("");
    }

    // If pool is exhausted, refresh pool status
    if (data?.poolExhausted) {
      fetchPoolStatus();
    }

    setSendTokenResponse({
      txHash: data?.txHash,
      message: data?.message,
    });

    // Refresh pool status and PEN balance after a claim
    fetchPoolStatus();
    if (address) checkPenBalance(address);

    setIsLoading(false);
    setClaimStatus("");
  }

  const back = (): void => {
    setSendTokenResponse({
      txHash: null,
      message: null,
    });

    checkRateLimit();
  };

  const setTokenInCookies = (token: string) => {
    try {
      const decodedToken = jwtDecode<TokenPayload>(token);
      const expiration = decodedToken.exp * 1000; // Convert to milliseconds
      const walletAddress = decodedToken.mm_address;
      // Store token and expiration in cookies
      Cookies.set("auth_token", token, {
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("token_expiration", expiration.toString(), {
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("wallet_address", walletAddress, {
        secure: true,
        sameSite: "strict",
      });
      checkRole();
      setIsAuthenticated(true);
      updateAddress(walletAddress);
      monitorTokenExpiration(expiration);
    } catch (error) {
      console.error("Invalid token", error);
      setIsAuthenticated(false);
    }
  };

  const monitorTokenExpiration = (expiration: number) => {
    const currentTime = Date.now();
    const timeUntilExpiration = expiration - currentTime;
    if (timeUntilExpiration > 0) {
      setTimeout(() => {
        handleTokenExpiration();
      }, timeUntilExpiration);
    } else {
      handleTokenExpiration();
    }
  };

  const handleTokenExpiration = () => {
    setIsAuthenticated(false);
    Cookies.remove("auth_token");
    Cookies.remove("token_expiration");
    Cookies.remove("wallet_address");
    window.location.reload();
  };
  const handleSignInSuccess = (e: CustomEvent) => {
    setTokenInCookies(e?.detail?.token);
  };

  useEffect(() => {
    // Check for token in URL (redirect from pentagon.games/sign-in)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token");
    if (urlToken) {
      setTokenInCookies(urlToken);
      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Check for existing auth cookies
    const token = Cookies.get("auth_token");
    const expiration = Cookies.get("token_expiration");
    const walletAddress = Cookies.get("wallet_address");
    if (token && expiration) {
      const expirationTime = parseInt(expiration, 10);
      if (Date.now() < expirationTime) {
        checkRole();
        setIsAuthenticated(true);
        updateAddress(walletAddress);
        monitorTokenExpiration(expirationTime);
      } else {
        handleTokenExpiration();
      }
    }
  }, []);
  useEffect(() => {
    if (window.PentagonWidget) {
      window.PentagonWidget.mount();
    }

    window.addEventListener(
      "PentagonGamesSignInOnSuccess",
      handleSignInSuccess
    );

    return () => {
      window.removeEventListener(
        "PentagonGamesSignInOnSuccess",
        handleSignInSuccess
      );
    };
  }, []);
  // Calculate per-tier drop from pool
  const TOTAL_WEIGHT = 810; // 70*1 + 29*10 + 9*50
  const CLAIMS_PER_WEEK = 7; // daily claims
  const poolTotal = poolStatus?.total ? parseFloat(poolStatus.total) / 1e18 : 0;
  const baseUnit = poolTotal / TOTAL_WEIGHT / CLAIMS_PER_WEEK;

  // Tier config for display
  const tierTable = [
    { tier: "VIP1", pen: "200,000", share: "1%", weight: 1, drop: (baseUnit * 1), highlight: false },
    { tier: "VIP2", pen: "1,000,000", share: "10%", weight: 10, drop: (baseUnit * 10), highlight: true },
    { tier: "VIP3", pen: "5,000,000", share: "50%", weight: 50, drop: (baseUnit * 50), highlight: false },
  ];

  // CURRENT gas collector / sequencer (live, fees actively flow here). The keeper
  // contract + backend checkpoint both read THIS address for the weekly burn delta.
  const GAS_COLLECTOR = "0x34DaDcB5010ffB1E355B92AD53bbEbECe98af471";
  // OLD/retired collector (frozen since 2026-06-01 migration). Shown for transparency only.
  const OLD_GAS_COLLECTOR = "0x02c7D29a0eA1dce127F75daa254F7aDab1EE9bb2";
  const EXPLORER_BASE = "https://explorer.pentagon.games";
  const KEEPER_ADDRESS = "0x80274BA0910A4a5dfc540F6E4542261C4A981BB3";
  const KEEPER_ABI = [
    "function topUp() external",
    "function preview() external view returns (uint256 delta, uint256 toSend, uint256 keeperBalance, bool canTopUp)",
    "function lastSnapshot() external view returns (uint256)",
    "function lastTopUpTime() external view returns (uint256)",
    "function totalTransferred() external view returns (uint256)",
  ];

  // Pool percentage for progress bar
  const poolPct = poolStatus?.active && poolStatus?.total && poolStatus?.remaining
    ? Math.round((parseFloat(poolStatus.remaining) / parseFloat(poolStatus.total)) * 100)
    : 0;

  if (tokenLoading) {
    return <ClipLoader size="20px" speedMultiplier={0.3} color="403F40" />;
  } else
    return (
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "30px 20px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "30px" }}>
          <h1 className="font" style={{
            fontSize: "32px",
            fontWeight: "bold",
            marginBottom: "4px",
            letterSpacing: "0.5px",
          }}>
            PEN VIP Daily Faucet
          </h1>
          <p className="font" style={{
            fontSize: "14px",
            opacity: 0.7,
            marginTop: 0,
            marginBottom: 0,
          }}>
            Hold PEN on Pentagon Chain. Claim PC gas once per day. Higher tier = bigger drop.
          </p>
        </div>

        {/* Launch announcement banner */}
        <div style={{
          maxWidth: "960px",
          margin: "0 auto 24px",
          padding: "14px 20px",
          borderRadius: "8px",
          background: "linear-gradient(135deg, #d32f2f, #b71c1c)",
          border: "1px solid #ff5252",
          textAlign: "center",
        }}>
          <p style={{
            color: "white",
            fontSize: "14px",
            fontWeight: "bold",
            margin: "0 0 6px 0",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}>
            🚀 New Faucet Requirements
          </p>
          <p style={{
            color: "white",
            fontSize: "13px",
            margin: "0 0 4px 0",
            lineHeight: "20px",
          }}>
            <strong>1.</strong> Get PEN VIP status to unlock faucet access.{" "}
            <strong>2.</strong> Bridge PEN to Pentagon Chain to set your on-chain tier and drop amount.
          </p>
          <p style={{
            color: "rgba(255,255,255,0.7)",
            fontSize: "11px",
            margin: 0,
          }}>
            More eligibility options (NFT holders, other assets on PC) will be added over time.
          </p>
        </div>

        {/* Two-column layout */}
        <div style={{
          display: "flex",
          gap: "24px",
          alignItems: "flex-start",
          flexWrap: "wrap",
          justifyContent: "center",
        }}>

          {/* LEFT: Login + Faucet */}
          <div style={{ flex: "1 1 460px", maxWidth: "500px", minWidth: "320px" }}>

            {/* Signed-in user bar (top of left column) */}
            {isAuthenticated && (
              <div style={{
                marginBottom: "12px",
                padding: "10px 16px",
                borderRadius: "8px",
                background: "rgba(231, 98, 243, 0.08)",
                border: "1px solid rgba(231, 98, 243, 0.2)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
                <div>
                  <span className="font" style={{ fontSize: "12px", opacity: 0.6 }}>Signed in as</span>
                  <p className="font" style={{ fontSize: "13px", margin: "2px 0 0", fontWeight: "bold" }}>
                    {inputAddress ? `${inputAddress.slice(0, 6)}...${inputAddress.slice(-4)}` : "Connected"}
                  </p>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "2px" }}>
                    {detectedTier && (
                      <span className="font" style={{ fontSize: "11px", color: "rgb(231, 98, 243)" }}>
                        {detectedTier}
                      </span>
                    )}
                    {onChainTier && (
                      <a href="/loyalty" style={{
                        fontSize: "11px",
                        color: "#FFD700",
                        background: "rgba(255, 215, 0, 0.1)",
                        padding: "2px 8px",
                        borderRadius: "3px",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}>
                        💎 {loyaltyPoints} pts ({loyaltyDays}d) — Loyalty Info ↗
                      </a>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button className="logout" onClick={handleTokenExpiration} style={{
                    padding: "6px 16px",
                    fontSize: "12px",
                  }}>
                    Sign Out
                  </button>
                </div>
              </div>
            )}

            {/* Pool Status */}
            {poolStatus && (
              <div style={{
                marginBottom: "16px",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid rgba(231, 98, 243, 0.2)",
                background: "rgba(27, 26, 27, 0.5)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span className="font" style={{ fontSize: "12px", opacity: 0.7 }}>
                    Weekly Pool
                  </span>
                  <span className="font" style={{ fontSize: "12px" }}>
                    {poolStatus.active
                      ? `${weiToPC(poolStatus.remaining)} / ${weiToPC(poolStatus.total)} PC`
                      : "Exhausted"}
                  </span>
                </div>
                <div style={{
                  width: "100%",
                  height: "6px",
                  borderRadius: "3px",
                  background: "rgba(255,255,255,0.1)",
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${poolPct}%`,
                    height: "100%",
                    borderRadius: "3px",
                    background: poolPct > 20
                      ? "linear-gradient(90deg, rgb(96, 64, 239), rgb(231, 98, 243))"
                      : "#ff5252",
                    transition: "width 0.3s ease",
                  }} />
                </div>
                <p className="font" style={{ fontSize: "10px", opacity: 0.5, marginTop: "6px", marginBottom: 0 }}>
                  50% of weekly PC burn funds this pool. Unclaimed amounts roll over.{" "}
                  <a
                    href={`${EXPLORER_BASE}/address/${GAS_COLLECTOR}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "rgb(231, 98, 243)", textDecoration: "underline" }}
                  >Check the burn ↗</a>
                </p>
              </div>
            )}

            {/* Main Faucet Box */}
            <div className="box">
              <div className="banner" />

              <div className="box-content">
                <div
                  style={{
                    display: sendTokenResponse?.txHash ? "none" : "block",
                  }}
                >
                  {poolStatus && !poolStatus.active && (
                    <p
                      className="rate-limit-text font"
                      style={{ color: "#ff5252", fontWeight: "bold" }}
                    >
                      Weekly pool is exhausted. Unclaimed PC rolls into next week. Check back Monday!
                    </p>
                  )}

                  {/* Tier + Eligibility Status */}
                  {isAuthenticated && detectedTier && (
                    <div style={{
                      padding: "10px 14px",
                      borderRadius: "6px",
                      marginBottom: "10px",
                      background: onChainTier
                        ? "rgba(76, 175, 80, 0.1)"
                        : "rgba(255, 82, 82, 0.1)",
                      border: `1px solid ${onChainTier ? "rgba(76, 175, 80, 0.3)" : "rgba(255, 82, 82, 0.3)"}`,
                    }}>
                      <p className="font" style={{ fontSize: "12px", margin: "0 0 4px" }}>
                        Discord VIP: <strong style={{ color: "rgb(231, 98, 243)" }}>{detectedTier}</strong>
                        {onChainTier && (
                          <> · On-chain: <strong style={{ color: "#4CAF50" }}>{onChainTier}</strong></>
                        )}
                      </p>
                      <div style={{ fontSize: "11px", margin: "0 0 6px", opacity: 0.8, display: "flex", gap: "12px", flexWrap: "wrap" }}>
                        <span className="font">
                          PEN on ETH: <strong>{penBalanceEth !== null ? penBalanceEth.toLocaleString() : "..."}</strong>
                        </span>
                        <span className="font">
                          PEN on PC: <strong style={{ color: (penBalance ?? 0) >= 200000 ? "#4CAF50" : "#ff5252" }}>{penBalance !== null ? penBalance.toLocaleString() : "..."}</strong>
                        </span>
                      </div>
                      {onChainTier ? (
                        <p className="font" style={{ fontSize: "12px", margin: 0, color: "#4CAF50", fontWeight: "bold" }}>
                          ✅ Eligible to claim
                          {poolStatus?.tierAmounts && onChainTier && (
                            <> — {weiToPC(poolStatus.tierAmounts[onChainTier])} PC this week</>
                          )}
                        </p>
                      ) : (
                        <div>
                          <p className="font" style={{ fontSize: "12px", margin: "0 0 6px", color: "#ff5252" }}>
                            ⚠️ You're {detectedTier} on Discord but need PEN on <strong>Pentagon Chain</strong> to claim.
                          </p>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            <a href="https://app.uniswap.org/swap?outputCurrency=0x5ee3188a3f8adee1d736edd4ae85000105c88f66"
                              target="_blank" rel="noreferrer"
                              style={{ fontSize: "11px", color: "rgb(231, 98, 243)", textDecoration: "none", padding: "4px 10px", borderRadius: "4px", border: "1px solid rgba(231, 98, 243, 0.3)", background: "rgba(231, 98, 243, 0.08)" }}>
                              Get PEN on Uniswap ↗
                            </a>
                            <a href="https://bridge.pentagon.games"
                              target="_blank" rel="noreferrer"
                              style={{ fontSize: "11px", color: "rgb(231, 98, 243)", textDecoration: "none", padding: "4px 10px", borderRadius: "4px", border: "1px solid rgba(231, 98, 243, 0.3)", background: "rgba(231, 98, 243, 0.08)" }}>
                              Bridge to Pentagon Chain ↗
                            </a>
                            {penBalance !== null && penBalance > 0 && penBalance < 200000 && (
                              <p className="font" style={{ fontSize: "11px", margin: 0, color: "#FF9800" }}>
                                Need {(200000 - penBalance).toLocaleString()} more PEN for VIP1.{" "}
                                <a href="https://app.uniswap.org/swap?outputCurrency=0x5ee3188a3f8adee1d736edd4ae85000105c88f66"
                                  target="_blank" rel="noreferrer"
                                  style={{ color: "rgb(231, 98, 243)" }}>
                                  Get more ↗
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {isAuthenticated && !detectedTier && !userRole && penBalance !== null && (
                    <div style={{
                      padding: "10px 14px",
                      borderRadius: "6px",
                      marginBottom: "10px",
                      background: "rgba(255, 152, 0, 0.1)",
                      border: "1px solid rgba(255, 152, 0, 0.3)",
                    }}>
                      <p className="font" style={{ fontSize: "12px", margin: 0, color: "#FF9800" }}>
                        PEN on Pentagon Chain: <strong>{penBalance.toLocaleString()}</strong>.
                        {penBalance < 200000 && ` Need ${(200000 - penBalance).toLocaleString()} more PEN for VIP1 status.`}
                      </p>
                    </div>
                  )}

                  <p
                    className="rate-limit-text font"
                    style={{ color: (retryMessage || cooldownSeconds > 0) ? "#ff5252" : undefined }}
                  >
                    {retryMessage
                      ? retryMessage
                      : cooldownSeconds > 0
                        ? `⏳ Cooldown active: ${Math.floor(cooldownSeconds / 3600)}h ${Math.floor((cooldownSeconds % 3600) / 60)}m remaining`
                        : "Drops are limited to 1 request per 24 hours."}
                  </p>
                  {!isAuthenticated ? (
                    <div
                      onClick={() => window.location.reload()}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "45px",
                        borderRadius: "5px",
                        background: "linear-gradient(135deg, rgb(96, 64, 239), rgb(231, 98, 243))",
                        cursor: "pointer",
                        marginBottom: "8px",
                        transition: "opacity 0.2s",
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.opacity = "0.85")}
                      onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
                    >
                      <span style={{ color: "white", fontWeight: "bold", fontSize: "15px", letterSpacing: "0.5px" }}>
                        🔐 Sign In with Pentagon to Get Address
                      </span>
                    </div>
                  ) : (
                    <div className="address-input">
                      <input
                        placeholder="Hexadecimal Address (0x...)"
                        value={inputAddress || ""}
                        onChange={(e) => updateAddress(e.target.value)}
                        autoFocus
                        disabled={true}
                      />
                    </div>
                  )}

                  <button
                    className={
                      shouldAllowSend && !userRole && !retryMessage && (poolStatus?.active !== false) && onChainTier && cooldownSeconds === 0 ? "send-button" : "send-button-disabled"
                    }
                    onClick={sendToken}
                    disabled={poolStatus?.active === false || !onChainTier || cooldownSeconds > 0}
                  >
                    {isLoading ? (
                      <span className="font" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                        <ClipLoader
                          size="16px"
                          speedMultiplier={0.3}
                          color="403F40"
                        />
                        {claimStatus || "Processing..."}
                      </span>
                    ) : poolStatus && !poolStatus.active ? (
                      <span className="font">Pool Exhausted</span>
                    ) : (
                      <span className="font">
                        Request PC
                      </span>
                    )}
                  </button>
                </div>

                <div
                  style={{
                    display: sendTokenResponse?.txHash ? "block" : "none",
                  }}
                >
                  <p className="rate-limit-text font">
                    {sendTokenResponse?.message}
                  </p>

                  <div>
                    <span className="bold-text font">Transaction ID</span>
                    <p className="rate-limit-text">
                      <a
                        target={"_blank"}
                        href={
                          EXPLORER_BASE +
                          "/tx/" +
                          sendTokenResponse?.txHash
                        }
                        rel="noreferrer"
                      >
                        {sendTokenResponse?.txHash}
                      </a>
                    </p>
                  </div>

                  <button className="back-button" onClick={back}>
                    Back
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Explainer */}
          <div style={{ flex: "1 1 380px", maxWidth: "460px", minWidth: "300px" }}>

            {/* VIP CTA - hidden for authenticated VIP users */}
            {(!isAuthenticated || (!detectedTier && !onChainTier)) && (
              <div style={{
                padding: "16px 20px",
                borderRadius: "8px",
                border: "1px solid rgba(231, 98, 243, 0.4)",
                background: "linear-gradient(135deg, rgba(96, 64, 239, 0.15), rgba(231, 98, 243, 0.1))",
                marginBottom: "16px",
                textAlign: "center",
              }}>
                <p className="font" style={{ fontSize: "15px", fontWeight: "bold", marginTop: 0, marginBottom: "6px" }}>
                  Got PEN? Get VIP.
                </p>
                <p className="font" style={{ fontSize: "12px", opacity: 0.8, marginTop: 0, marginBottom: "12px" }}>
                  Only PEN VIP grants faucet access. Other VIP programs do not qualify.
                </p>
                <a
                  href="https://vip.pentagon.games"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-block",
                    padding: "8px 24px",
                    borderRadius: "4px",
                    background: "rgb(231, 98, 243)",
                    color: "white",
                    textDecoration: "none",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  Get PEN VIP →
                </a>
              </div>
            )}

            {/* VIP Tier Table */}
            <div style={{
              marginBottom: "16px",
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid rgba(231, 98, 243, 0.3)",
            }}>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}>
                <thead>
                  <tr style={{ background: "rgba(231, 98, 243, 0.15)" }}>
                    <th className="font" style={{ padding: "8px 12px", textAlign: "left" }}>On-chain Tier</th>
                    <th className="font" style={{ padding: "8px 12px", textAlign: "right" }}>PEN Required</th>
                    <th className="font" style={{ padding: "8px 12px", textAlign: "right" }}>Share</th>
                    <th className="font" style={{ padding: "8px 12px", textAlign: "right" }}>Per Daily Claim</th>
                  </tr>
                </thead>
                <tbody>
                  {tierTable.map((row) => (
                    <tr key={row.tier} style={{
                      background: onChainTier === row.tier
                        ? "rgba(231, 98, 243, 0.1)"
                        : "transparent",
                      borderTop: "1px solid rgba(255,255,255,0.05)",
                    }}>
                      <td className="font" style={{
                        padding: "8px 12px",
                        fontWeight: onChainTier === row.tier ? "bold" : "normal",
                      }}>
                        {row.tier}
                        {onChainTier === row.tier && " ◀"}
                        {row.highlight && (
                          <span style={{
                            marginLeft: "6px",
                            fontSize: "10px",
                            background: "rgba(231, 98, 243, 0.3)",
                            padding: "1px 5px",
                            borderRadius: "3px",
                          }}>BEST VALUE</span>
                        )}
                      </td>
                      <td className="font" style={{ padding: "8px 12px", textAlign: "right" }}>
                        {row.pen}
                      </td>
                      <td className="font" style={{ padding: "8px 12px", textAlign: "right" }}>
                        {row.share}
                      </td>
                      <td className="font" style={{ padding: "8px 12px", textAlign: "right", fontWeight: "bold", color: "rgb(231, 98, 243)" }}>
                        {row.drop > 0 ? `${row.drop.toFixed(4)} PC` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* How it works */}
            <div style={{
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid rgba(231, 98, 243, 0.15)",
              background: "rgba(27, 26, 27, 0.3)",
              marginBottom: "16px",
            }}>
              <p className="font" style={{ fontSize: "14px", fontWeight: "bold", marginTop: 0, marginBottom: "10px" }}>
                How does it work?
              </p>
              <p className="font" style={{ fontSize: "12px", opacity: 0.85, lineHeight: "19px", marginTop: 0, marginBottom: "10px" }}>
                <strong>Step 1: Get VIP access.</strong> Earn any PEN VIP status (via PEN holdings) to unlock the faucet.
                Verify your role in the Pentagon Games Discord.
              </p>
              <p className="font" style={{ fontSize: "12px", opacity: 0.85, lineHeight: "19px", marginTop: 0, marginBottom: "10px" }}>
                <strong>Step 2: Bridge PEN to Pentagon Chain.</strong> Your on-chain PEN balance on Pentagon Chain
                determines your tier and drop amount. More PEN on PC = bigger daily drop.
              </p>
              <p className="font" style={{ fontSize: "12px", opacity: 0.85, lineHeight: "19px", marginTop: 0, marginBottom: "10px" }}>
                Every week, 50% of the PC burned as gas on Pentagon Chain funds this faucet pool.
                Claim once per day. Unclaimed PC rolls into the next week, growing the pool over time.
              </p>
              <p className="font" style={{ fontSize: "11px", opacity: 0.5, marginTop: 0, marginBottom: 0 }}>
                VIP2 offers the best ROI per PEN held. VIP3 is for whales who want max drop in a single wallet.
              </p>
            </div>

            {/* Keeper Top-Up + Burn Info */}
            <div style={{
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid rgba(231, 98, 243, 0.15)",
              background: "rgba(27, 26, 27, 0.3)",
            }}>
              <p className="font" style={{ fontSize: "13px", fontWeight: "bold", marginTop: 0, marginBottom: "8px", color: "rgb(231, 98, 243)" }}>
                ⛽ Weekly Pool Top-Up
              </p>
              <p className="font" style={{ fontSize: "11px", opacity: 0.6, marginTop: 0, marginBottom: "8px" }}>
                Every week, 50% of new PC burned as gas on Pentagon Chain gets added to the faucet pool. This runs automatically, but anyone can also trigger it manually. The keeper contract reads the gas collector balance, calculates what's new since last top-up, and sends half to the faucet.
              </p>
              {keeperPreview && (
                <div style={{ fontSize: "11px", opacity: 0.7, marginBottom: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                    <span className="font">New gas since last top-up:</span>
                    <span className="font">{weiToPC(keeperPreview.delta)} PC</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                    <span className="font">50% to add to pool:</span>
                    <span className="font" style={{ color: "rgb(76, 175, 80)" }}>{weiToPC(keeperPreview.toSend)} PC</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                    <span className="font">Keeper balance:</span>
                    <span className="font">{weiToPC(keeperPreview.keeperBalance)} PC</span>
                  </div>
                  {keeperPreview.totalTransferred !== "0" && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                      <span className="font">Total transferred to date:</span>
                      <span className="font">{weiToPC(keeperPreview.totalTransferred)} PC</span>
                    </div>
                  )}
                </div>
              )}

              {/* Weekly BURN snapshot — makes this week's pool source explicit */}
              <div style={{
                fontSize: "11px",
                marginBottom: "10px",
                padding: "8px 10px",
                borderRadius: "6px",
                background: "rgba(231, 98, 243, 0.06)",
                border: "1px solid rgba(231, 98, 243, 0.12)",
              }}>
                <div className="font" style={{ fontWeight: "bold", marginBottom: "6px", opacity: 0.85 }}>
                  🔥 This week's burn snapshot{poolStatus?.weekStart ? ` (week of ${poolStatus.weekStart})` : ""}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                  <span className="font" style={{ opacity: 0.7 }}>Weekly pool (50% of burn):</span>
                  <span className="font" style={{ color: "rgb(76, 175, 80)" }}>
                    {poolStatus?.total ? `${weiToPC(poolStatus.total)} PC` : "—"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                  <span className="font" style={{ opacity: 0.7 }}>Remaining this week:</span>
                  <span className="font">
                    {poolStatus?.remaining ? `${weiToPC(poolStatus.remaining)} PC` : "—"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span className="font" style={{ opacity: 0.7 }}>Implied total burn:</span>
                  <span className="font">
                    {poolStatus?.total ? `~${(parseFloat(poolStatus.total) / 1e18 * 2).toFixed(4)} PC` : "—"}
                  </span>
                </div>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "6px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                    <span className="font" style={{ opacity: 0.7 }}>
                      <span style={{ color: "rgb(76, 175, 80)" }}>●</span> Current collector:
                    </span>
                    <a
                      href={`${EXPLORER_BASE}/address/${GAS_COLLECTOR}`}
                      target="_blank" rel="noreferrer"
                      className="font"
                      style={{ color: "rgb(231, 98, 243)", textDecoration: "underline" }}
                    >{GAS_COLLECTOR.slice(0, 6)}…{GAS_COLLECTOR.slice(-4)} ↗</a>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="font" style={{ opacity: 0.45 }}>
                      <span>○</span> Old (retired) collector:
                    </span>
                    <a
                      href={`${EXPLORER_BASE}/address/${OLD_GAS_COLLECTOR}`}
                      target="_blank" rel="noreferrer"
                      className="font"
                      style={{ color: "rgba(255,255,255,0.4)", textDecoration: "line-through" }}
                    >{OLD_GAS_COLLECTOR.slice(0, 6)}…{OLD_GAS_COLLECTOR.slice(-4)} ↗</a>
                  </div>
                </div>
              </div>

              <button
                onClick={handleTopUp}
                disabled={topUpLoading || (keeperPreview && !keeperPreview.canTopUp)}
                className="font"
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: keeperPreview?.canTopUp ? "pointer" : "not-allowed",
                  background: keeperPreview?.canTopUp
                    ? "linear-gradient(90deg, rgb(96, 64, 239), rgb(231, 98, 243))"
                    : "rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: "bold",
                  opacity: keeperPreview?.canTopUp ? 1 : 0.5,
                }}
              >
                {topUpLoading ? "Processing..." : keeperPreview?.canTopUp ? `Top Up Pool (+${weiToPC(keeperPreview.toSend)} PC)` : keeperPreview?.keeperBalance === "0" ? "Keeper needs funding" : keeperPreview?.delta === "0" ? "No new gas to collect" : `Next top-up available in ~${Math.max(0, Math.ceil(((keeperPreview?.lastTopUpTime || 0) + 518400 - Date.now()/1000) / 86400))} days`}
              </button>
              {topUpResult && (
                <p className="font" style={{ fontSize: "11px", marginTop: "6px", marginBottom: 0, color: topUpResult.includes("successful") ? "rgb(76, 175, 80)" : "rgb(231, 98, 243)" }}>
                  {topUpResult}
                </p>
              )}
              {/* On-chain flow, public for verification: Collector → Keeper → Faucet */}
              <div style={{ marginTop: "12px", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "8px" }}>
                <p className="font" style={{ fontSize: "10px", opacity: 0.5, margin: "0 0 6px" }}>
                  On-chain flow (verify on explorer): Gas Collector → Keeper → Faucet
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
                  <a
                    href={`${EXPLORER_BASE}/address/${GAS_COLLECTOR}`}
                    target="_blank" rel="noreferrer"
                    className="font"
                    style={{ fontSize: "10px", color: "rgb(231, 98, 243)", opacity: 0.8 }}
                  >🔥 Gas Collector ↗</a>
                  <a
                    href={`${EXPLORER_BASE}/address/${KEEPER_ADDRESS}`}
                    target="_blank" rel="noreferrer"
                    className="font"
                    style={{ fontSize: "10px", color: "rgb(231, 98, 243)", opacity: 0.8 }}
                  >📋 Keeper Contract ↗</a>
                  <a
                    href={`${EXPLORER_BASE}/address/0x6E5F8fB602dD598E0C30ec2eD29D0F0D81a8f464`}
                    target="_blank" rel="noreferrer"
                    className="font"
                    style={{ fontSize: "10px", color: "rgb(231, 98, 243)", opacity: 0.8 }}
                  >🏦 Faucet Contract ↗</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ErrorRoleModal isOpen={isAuthenticated && userRole} />
        <LoginModal isOpen={!isAuthenticated} onLoginSuccess={(token) => setTokenInCookies(token)} />
      </div>
    );
};

export default FaucetForm;
